import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';

export default function Settings() {
    const { branding, refreshBranding } = useOutletContext();
    const [form, setForm] = useState({ companyName: '', logoText: '', tagline: '', address: '', gstin: '', phone: '', displayEmail: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (branding) {
            setForm({
                companyName:  branding.name        || '',
                logoText:     branding.logoText     || branding.name || '',
                tagline:      branding.tagline      || '',
                address:      branding.address      || '',
                gstin:        branding.gstin        || '',
                phone:        branding.phone        || '',
                displayEmail: branding.email        || '',
            });
        }
    }, [branding]);

    const handleSave = async (e) => {
        e.preventDefault();
        setIsSaving(true); setSaved(false);
        try {
            await axios.put('/api/profile', form);
            if (refreshBranding) await refreshBranding();
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch { alert('Save failed — make sure backend is running'); }
        finally { setIsSaving(false); }
    };

    const inp = (label, key, placeholder, type = 'text') => (
        <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>{label}</label>
            <input
                type={type}
                value={form[key]}
                onChange={e => setForm({ ...form, [key]: e.target.value })}
                placeholder={placeholder}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '9px', border: '1.5px solid #e2e8f0', fontSize: '14px', fontFamily: 'Inter,sans-serif', color: '#1e293b', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', transition: 'all 0.2s' }}
                onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.background = '#fff'; e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)'; }}
                onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; e.target.style.boxShadow = 'none'; }}
            />
        </div>
    );

    return (
        <div style={{ maxWidth: '680px', fontFamily: 'Inter,sans-serif' }}>
            {/* Header */}
            <div style={{ marginBottom: '28px' }}>
                <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '800', color: '#0f172a' }}>⚙️ Company Settings</h2>
                <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#94a3b8' }}>
                    Update your company branding — changes reflect on all invoices, POs, and quotes instantly.
                </p>
            </div>

            {saved && (
                <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#16a34a', padding: '12px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: '600', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    ✅ Settings saved! All documents will now use the updated branding.
                </div>
            )}

            <form onSubmit={handleSave}>
                {/* Logo Preview */}
                <div style={{ background: '#0f172a', borderRadius: '14px', padding: '24px 28px', marginBottom: '28px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div>
                        <div style={{ fontSize: '24px', fontWeight: '900', color: '#60a5fa', letterSpacing: '-0.5px' }}>
                            {form.logoText || form.companyName || 'Your Company'}
                        </div>
                        {form.tagline && <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>{form.tagline}</div>}
                    </div>
                    <div style={{ marginLeft: 'auto', fontSize: '11px', color: '#475569', background: 'rgba(255,255,255,0.07)', borderRadius: '8px', padding: '6px 12px' }}>
                        Live preview
                    </div>
                </div>

                {/* Logo / Display Name */}
                <div style={{ background: '#fff', borderRadius: '14px', padding: '24px 28px', border: '1px solid #f1f5f9', boxShadow: '0 2px 12px -4px rgba(0,0,0,0.06)', marginBottom: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '1.5px', color: '#64748b', textTransform: 'uppercase', marginBottom: '16px' }}>🏷️ Logo & Display</div>
                    {inp('Company Name *', 'companyName', 'e.g. Megahertz Infotech Pvt. Ltd.')}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>
                            Logo Text <span style={{ color: '#94a3b8', fontWeight: '400' }}>(shown on invoice header — can be shorter than company name)</span>
                        </label>
                        <input
                            type="text"
                            value={form.logoText}
                            onChange={e => setForm({ ...form, logoText: e.target.value })}
                            placeholder="e.g. MegahertzInfotech"
                            style={{ width: '100%', padding: '10px 14px', borderRadius: '9px', border: '1.5px solid #e2e8f0', fontSize: '14px', fontFamily: 'Inter,sans-serif', color: '#1e293b', background: '#f8fafc', outline: 'none', boxSizing: 'border-box' }}
                            onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.background = '#fff'; }}
                            onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                        />
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>Leave blank to use Company Name</div>
                    </div>
                    {inp('Tagline / Subtitle', 'tagline', 'e.g. Cybersecurity & Resource Deployment')}
                </div>

                {/* Contact Details */}
                <div style={{ background: '#fff', borderRadius: '14px', padding: '24px 28px', border: '1px solid #f1f5f9', boxShadow: '0 2px 12px -4px rgba(0,0,0,0.06)', marginBottom: '16px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '800', letterSpacing: '1.5px', color: '#64748b', textTransform: 'uppercase', marginBottom: '16px' }}>📍 Business Details</div>
                    <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '6px' }}>Business Address</label>
                        <textarea
                            value={form.address}
                            onChange={e => setForm({ ...form, address: e.target.value })}
                            placeholder="Street, City, State, PIN"
                            rows={3}
                            style={{ width: '100%', padding: '10px 14px', borderRadius: '9px', border: '1.5px solid #e2e8f0', fontSize: '14px', fontFamily: 'Inter,sans-serif', color: '#1e293b', background: '#f8fafc', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
                            onFocus={e => { e.target.style.borderColor = '#6366f1'; e.target.style.background = '#fff'; }}
                            onBlur={e => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = '#f8fafc'; }}
                        />
                    </div>
                    {inp('GSTIN', 'gstin', 'e.g. 07AADCM3636J1ZE')}
                    {inp('Phone Number', 'phone', 'e.g. 011-46644000')}
                    {inp('Display Email', 'displayEmail', 'e.g. orders@megahertz.com', 'email')}
                </div>

                {/* Save button */}
                <button
                    type="submit"
                    disabled={isSaving}
                    style={{ width: '100%', padding: '15px', background: isSaving ? '#94a3b8' : 'linear-gradient(135deg,#6366f1,#4f46e5)', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'Inter,sans-serif', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                    {isSaving ? '⏳ Saving...' : '💾 Save Company Settings'}
                </button>
            </form>
        </div>
    );
}
